import type { AppMode } from '@iheartjane/types';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';

interface CartDrawerContextValues {
  close: () => void;
  isOpen: boolean;
  /** Shows the bag: routes in bloom/headless, opens the drawer everywhere else. */
  open: () => void;
  /** Headless renders the v2 drawer route; sends `open` there, not to the cart page. */
  routeBagToDrawer: () => void;
}

// Exported for the test helper only; consumers go through the two hooks.
export const CartDrawerContext = createContext<CartDrawerContextValues | null>(
  null
);
CartDrawerContext.displayName = 'CartDrawer';

/**
 * For product cards shared with apps that render no cart drawer at all
 * (business-admin). Returns `null` outside a provider instead of throwing.
 */
export const useSafeCartDrawer = () => useContext(CartDrawerContext);

export const useCartDrawer = () =>
  useContextEnsuringProvider(CartDrawerContext);

/**
 * Owns how the cart drawer is shown. Client-invented UI state: no API sends
 * `is_open` or `headless_v2`, and nothing persists either.
 *
 * Must be mounted inside a router — `open` navigates in bloom and headless,
 * where the bag is a route rather than a mounted drawer.
 */
export const CartDrawerProvider = ({
  appMode,
  children,
}: PropsWithChildren<{ appMode: AppMode }>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [bagRoutesToDrawer, setBagRoutesToDrawer] = useState(false);
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLElement | null>(null);

  // Safe to take the raw appMode: `bloom` and `headless` share this branch, and
  // only the headless app can derive `bloom` (via `bloomUserExperience`).
  const routesToBag = appMode === 'bloom' || appMode === 'headless';

  const close = useCallback(() => {
    setIsOpen(false);
    const trigger = triggerRef.current;
    if (trigger) {
      triggerRef.current = null;
      requestAnimationFrame(() => trigger.focus());
    }
  }, []);
  const routeBagToDrawer = useCallback(() => setBagRoutesToDrawer(true), []);

  const open = useCallback(() => {
    triggerRef.current = document.activeElement as HTMLElement | null;
    if (routesToBag) {
      // These two routes are the iframe's *entry points*, not navigation between pages: the
      // v1 embed boots the frame at /headless/cart (PendingCart), the v2 embed
      // at /headless/drawer (HeadlessCartDrawer)
      navigate(bagRoutesToDrawer ? '/headless/drawer' : '/headless/cart');
    } else {
      setIsOpen(true);
    }
  }, [bagRoutesToDrawer, navigate, routesToBag]);

  const value = useMemo(
    () => ({ close, isOpen, open, routeBagToDrawer }),
    [close, isOpen, open, routeBagToDrawer]
  );

  return (
    <CartDrawerContext.Provider value={value}>
      {children}
    </CartDrawerContext.Provider>
  );
};
